import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVisits, useCloseVisit } from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { joinFullName } from '../../utils/patientName';
import {
  Calendar,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Stethoscope,
  Bed,
  Ambulance,
  ExternalLink,
  Search,
  X,
} from 'lucide-react';

const visitTypeIcons = {
  OPD: Stethoscope,
  IPD: Bed,
  ER: Ambulance,
  EXT: ExternalLink,
};

const visitTypeColors = {
  OPD: 'bg-blue-100 text-blue-700',
  IPD: 'bg-purple-100 text-purple-700',
  ER: 'bg-red-100 text-red-700',
  EXT: 'bg-gray-100 text-gray-700',
};

const statusColors = {
  ACTIVE: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-blue-100 text-blue-700',
  CANCELLED: 'bg-red-100 text-red-700',
  DISCHARGED: 'bg-gray-100 text-gray-700',
};

export function Visits() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [visitType, setVisitType] = useState(() => params.get('visitType') || '');
  const [status, setStatus] = useState(() => params.get('status') || '');
  const [closeTarget, setCloseTarget] = useState<{ id: string; label: string } | null>(null);

  const filters = {
    search: debouncedSearch || undefined,
    visitType: visitType || undefined,
    status: status || undefined,
  };

  const { data, isLoading, error } = useVisits(page, limit, filters);
  const closeVisit = useCloseVisit();

  const visits = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (visitType) newParams.set('visitType', visitType);
    if (status) newParams.set('status', status);

    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, visitType, status, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search, visitType, status]);

  const handleCloseVisit = () => {
    if (!closeTarget) return;
    closeVisit.mutate(closeTarget.id, { onSuccess: () => setCloseTarget(null) });
  };

  const clearFilters = () => {
    setSearch('');
    setVisitType('');
    setStatus('');
    setPage(1);
  };

  const hasActiveFilters = search || visitType || status;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Visits</h1>
        </div>
        <button
          onClick={() => navigate('/visits/new')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          New Visit
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by visit number or patient..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <select
            value={visitType}
            onChange={(e) => setVisitType(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
          >
            <option value="">All Types</option>
            <option value="OPD">OPD</option>
            <option value="IPD">IPD</option>
            <option value="ER">Emergency</option>
            <option value="EXT">External</option>
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="DISCHARGED">Discharged</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load visits. Please try again.</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Visits Table */}
      {!isLoading && (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Visit #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visits.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      {hasActiveFilters ? (
                        <span>No visits match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No visits found'
                      )}
                    </td>
                  </tr>
                ) : (
                  visits.map((visit: any) => {
                    const TypeIcon = visitTypeIcons[visit.visitType as keyof typeof visitTypeIcons] || Calendar;
                    return (
                      <tr key={visit.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-sm font-mono text-text">{visit.visitNumber}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-text-muted" />
                            <span className="text-sm text-text">
                              {joinFullName(visit.patient || {})}
                            </span>
                          </div>
                          <span className="text-xs text-text-muted">{visit.patient?.registrationNumber}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${visitTypeColors[visit.visitType as keyof typeof visitTypeColors]}`}>
                            <TypeIcon size={12} />
                            {visit.visitType}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[visit.status as keyof typeof statusColors]}`}>
                            {visit.status === 'ACTIVE' && <Clock size={12} />}
                            {visit.status === 'COMPLETED' && <CheckCircle size={12} />}
                            {visit.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-text-muted">
                            {new Date(visit.visitDate).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {visit.status === 'ACTIVE' && (
                            <button
                              onClick={() => setCloseTarget({ id: visit.id, label: joinFullName(visit.patient || {}) || visit.visitNumber || 'this visit' })}
                              className="text-sm text-danger hover:text-danger-dark transition-colors"
                            >
                              Close
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="visits"
          />
        </div>
      )}

      <ConfirmDialog
        open={!!closeTarget}
        title="Close visit?"
        description={
          <>
            This will close the visit for{' '}
            <strong className="text-text">{closeTarget?.label}</strong>. You will not be able to add
            new charges or records to it afterwards.
          </>
        }
        confirmLabel="Close visit"
        loading={closeVisit.isPending}
        onConfirm={handleCloseVisit}
        onCancel={() => setCloseTarget(null)}
      />
    </div>
  );
}
